/* eslint-disable react-refresh/only-export-components */
import type { FormTableColumn } from '@connected/form-fields/components/form-table';
import type { Option, OptionValue } from '@connected/form-fields/types';

import {
    InputWithDropdown,
    NumberField,
    TextField,
} from '@connected/form-fields/components/form-table';

import type { NavItem } from '../../distribution-board-device/components/shared';
import type {
    DeviceActions,
    DeviceFieldName,
    DeviceForm,
    DeviceFormValues,
    DevicePatcher,
    FuseKind,
    SelectOption,
} from '../../distribution-board-device/types/hook-types';

import { RisoFieldCell, RowActionsCell } from '../../distribution-board-device/components/shared';
import {
    FUSE_KIND_OPTIONS,
    getFieldUIType,
    getRatedCurrentOptions,
    getTrippingCharacteristicOptions,
} from '../../distribution-board-device/config';

// ============================================================================
// Types
// ============================================================================

export type FCPDColumnConfig = {
    form: DeviceForm;
    createPatcher: (deviceId: string) => DevicePatcher;
    getFieldOptions: (fieldName: DeviceFieldName, deviceIndex: number) => SelectOption[];
    isFieldDisabled: (fieldName: DeviceFieldName, deviceIndex: number) => boolean;
    actions: DeviceActions;
    enableExtendedRiso: boolean;
    enableImpedanceMeasurementsPage: boolean;
    rowProps?: {
        electricalProjectVersionId: string;
        electricalProjectDistributionBoardId: string;
        page: number;
    };
};

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Tripping characteristic field that depends on fuseKind.
 * Renders SelectField, InputWithDropdown, or TextField based on parent/current values.
 */
function DependentTrippingCharacteristicCell({
    form,
    rowIndex,
    patcher,
    disabled,
}: {
    form: DeviceForm;
    rowIndex: number;
    patcher: DevicePatcher;
    disabled: boolean;
}) {
    return (
        <form.Subscribe
            selector={state => [
                state.values.devices[rowIndex]?.fuseKind,
                state.values.devices[rowIndex]?.trippingCharacteristic,
            ] as const}
        >
            {([fuseKind, trippingCharacteristic]) => {
                const options = getTrippingCharacteristicOptions(fuseKind as FuseKind | string | null);
                const baseUIType = getFieldUIType('trippingCharacteristic', fuseKind, trippingCharacteristic);
                const isValueInOptions = options.some(option => option.value === trippingCharacteristic);
                const uiType = baseUIType === 'SelectField' && !isValueInOptions
                    ? 'InputWithDropdown'
                    : baseUIType;

                return (
                    <form.Field
                        name={`devices[${rowIndex}].trippingCharacteristic`}
                        validators={{
                            onChangeAsync: patcher.patchField('trippingCharacteristic'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <>
                                {(uiType === 'SelectField' || uiType === 'InputWithDropdown') && (
                                    <InputWithDropdown
                                        field={field as any}
                                        options={options}
                                        inputType="text"
                                        placeholder="Typ of selecteer..."
                                        disabled={disabled}
                                    />
                                )}
                                {uiType === 'TextField' && (
                                    <TextField
                                        field={field as any}
                                        placeholder="Typ waarde..."
                                        disabled={disabled}
                                    />
                                )}
                            </>
                        )}
                    </form.Field>
                );
            }}
        </form.Subscribe>
    );
}

/**
 * Breaker rated current field that depends on fuseKind and trippingCharacteristic.
 * Renders SelectField, InputWithDropdown, or NumberField based on parent/current values.
 */
function DependentBreakerRatedCurrentCell({
    form,
    rowIndex,
    patcher,
    disabled,
}: {
    form: DeviceForm;
    rowIndex: number;
    patcher: DevicePatcher;
    disabled: boolean;
}) {
    return (
        <form.Subscribe
            selector={state => [
                state.values.devices[rowIndex]?.fuseKind,
                state.values.devices[rowIndex]?.trippingCharacteristic,
                state.values.devices[rowIndex]?.breakerRatedCurrent,
            ] as const}
        >
            {([fuseKind, trippingCharacteristic, breakerRatedCurrent]) => {
                const options = getRatedCurrentOptions(
                    fuseKind as FuseKind | string | null,
                    trippingCharacteristic,
                );

                // Determine UI type based on parent values and current value
                // If no options are available (e.g., fuseKind is custom), use NumberField
                // This handles the cascade where a custom fuseKind means no options for breakerRatedCurrent
                const baseUIType = getFieldUIType('breakerRatedCurrent', trippingCharacteristic, breakerRatedCurrent);
                const isValueInOptions = options.some(option => option.value === breakerRatedCurrent);
                const uiType = options.length === 0
                    ? 'NumberField'
                    : baseUIType === 'SelectField' && !isValueInOptions
                        ? 'InputWithDropdown'
                        : baseUIType;

                return (
                    <form.Field
                        name={`devices[${rowIndex}].breakerRatedCurrent`}
                        validators={{
                            onChangeAsync: patcher.patchField('breakerRatedCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <>
                                {(uiType === 'SelectField' || uiType === 'InputWithDropdown') && (
                                    <InputWithDropdown
                                        field={field as any}
                                        options={options}
                                        inputType="number"
                                        placeholder="Typ of selecteer..."
                                        disabled={disabled}
                                    />
                                )}
                                {uiType === 'NumberField' && (
                                    <NumberField
                                        field={field as any}
                                        placeholder="Typ waarde..."
                                        disabled={disabled}
                                    />
                                )}
                            </>
                        )}
                    </form.Field>
                );
            }}
        </form.Subscribe>
    );
}

// ============================================================================
// Column Factory
// ============================================================================

/**
 * Create FCPD columns based on configuration.
 * Column visibility is controlled by enableExtendedRiso and enableImpedanceMeasurementsPage flags.
 */
export function createFCPDColumns(config: FCPDColumnConfig): FormTableColumn<DeviceFormValues>[] {
    const {
        form: configForm,
        createPatcher,
        isFieldDisabled,
        actions,
        enableExtendedRiso,
        rowProps,
    } = config;

    const columns: FormTableColumn<DeviceFormValues>[] = [
        // Identifier (Naam)
        {
            accessorKey: 'identifier',
            header: 'Naam',
            size: '95px',
            minSize: '95px',
            sticky: true,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: ({ value }) => patcher.patchField('identifier')({ value: value ?? '' }),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <TextField field={field} placeholder="Naam" />
                        )}
                    </form.Field>
                );
            },
        },

        // FuseKind (Type beveiliging)
        // Renders SelectField for known values, InputWithDropdown for custom/manual input
        {
            accessorKey: 'fuseKind',
            header: 'Type beveiliging',
            size: '180px',
            minSize: '180px',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Subscribe selector={state => state.values.devices.find((d: DeviceFormValues) => d.id === row.id)?.fuseKind}>
                        {(fuseKind) => {
                            const uiType = getFieldUIType('fuseKind', null, fuseKind);

                            return (
                                <form.Field
                                    name={fieldName}
                                    validators={{
                                        onChangeAsync: patcher.patchField('fuseKind'),
                                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                    }}
                                >
                                    {field => (
                                        <>
                                            {(uiType === 'SelectField' || uiType === 'InputWithDropdown') && (
                                                <InputWithDropdown
                                                    field={field as any}
                                                    options={FUSE_KIND_OPTIONS as Option<OptionValue>[]}
                                                    inputType="text"
                                                    placeholder="Typ of selecteer..."
                                                />
                                            )}
                                        </>
                                    )}
                                </form.Field>
                            );
                        }}
                    </form.Subscribe>
                );
            },
        },

        // TrippingCharacteristic (Soort) - dependent on fuseKind
        {
            accessorKey: 'trippingCharacteristic',
            header: 'Soort',
            size: '180px',
            minSize: '180px',
            cell: ({ row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <DependentTrippingCharacteristicCell
                        form={configForm}
                        rowIndex={rowIndex}
                        patcher={patcher}
                        disabled={isFieldDisabled('trippingCharacteristic', rowIndex)}
                    />
                );
            },
        },

        // BreakerRatedCurrent (Waarde) - dependent on fuseKind + trippingCharacteristic
        {
            accessorKey: 'breakerRatedCurrent',
            header: 'Waarde (A)',
            size: '120px',
            minSize: '120px',
            cell: ({ row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <DependentBreakerRatedCurrentCell
                        form={configForm}
                        rowIndex={rowIndex}
                        patcher={patcher}
                        disabled={isFieldDisabled('breakerRatedCurrent', rowIndex)}
                    />
                );
            },
        },

        // RisoValue - conditional based on enableExtendedRiso
        {
            accessorKey: 'risoValue',
            header: 'Riso (MΩ)',
            size: '140px',
            minSize: '140px',
            visible: !enableExtendedRiso,
            cell: ({ row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <RisoFieldCell
                        form={configForm}
                        rowIndex={rowIndex}
                        patcher={patcher}
                        disabled={false}
                    />
                );
            },
        },

        // Impedance fields - conditional based on enableImpedanceMeasurementsPage
        // Zi fields
        {
            accessorKey: 'ziResistance',
            header: 'Zi (Ω)',
            size: '100px',
            minSize: '100px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziResistance'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'ziCurrent',
            header: 'Ik (Zi)',
            size: '100px',
            minSize: '100px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'ziMinimalCurrent',
            header: 'Acceptatiecriteria Zi',
            size: '175px',
            minSize: '175px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ziMinimalCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },

        // Zs fields
        {
            accessorKey: 'zsResistance',
            header: 'Zs (Ω)',
            size: '100px',
            minSize: '100px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsResistance'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'zsCurrent',
            header: 'Ik (Zs)',
            size: '100px',
            minSize: '100px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },
        {
            accessorKey: 'zsMinimalCurrent',
            header: 'Acceptatiecriteria Zs',
            size: '175px',
            minSize: '175px',
            visible: false,
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('zsMinimalCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField field={field as any} />
                        )}
                    </form.Field>
                );
            },
        },

        // Actions column
        {
            accessorKey: 'id',
            header: '',
            size: '32px',
            minSize: '32px',
            sticky: true,
            cell: ({ row }) => {
                // FCPD has an extra nav item for resistance measurements
                const extraNavItems: NavItem[] | undefined = rowProps
                    ? [{
                            label: 'Isolatieweerstandmetingen',
                            to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/devices/$distributionBoardDeviceId/resistance-measurements',
                            params: {
                                projectVersionId: rowProps.electricalProjectVersionId,
                                distributionboardId: rowProps.electricalProjectDistributionBoardId,
                                distributionBoardDeviceId: row.id,
                            },
                            search: { page: rowProps.page },
                        }]
                    : undefined;

                return (
                    <RowActionsCell
                        deviceId={row.id}
                        actions={actions}
                        rowProps={rowProps}
                        extraNavItems={extraNavItems}
                    />
                );
            },
        },
    ];

    return columns;
}
